import { useState } from 'react';
import Code from '@/components/dashboard/Code';
import { Link } from 'react-router-dom';
import { Waypoints, Zap, Database, Building2, Activity, } from 'lucide-react';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

function TabCode({ tabs }) {
  const [active, setActive] = useState(Object.keys(tabs)[0]);
  return (
    <div className="rounded-lg overflow-hidden border border-gray-200">
      <div className="flex border-b border-gray-200 bg-gray-50">
        {Object.keys(tabs).map((lang) => (
          <button
            key={lang}
            onClick={() => setActive(lang)}
            className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              active === lang
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {lang}
          </button>
        ))}
      </div>
      <div className="bg-gray-50">
        <Code lang={tabs[active].lang} noRound={true}>{tabs[active].code}</Code>
      </div>
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-6">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full border border-gray-300 bg-white flex items-center justify-center text-sm font-semibold text-gray-600 shrink-0">
          {number}
        </div>
        <div className="w-px flex-1 bg-gray-200 mt-3" />
      </div>
      <div className="pb-12 flex-1 min-w-0">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 mt-1">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 my-4">
      <Zap size={16} className="text-blue-500 mt-0.5 shrink-0" />
      <p className="text-sm text-blue-700">{children}</p>
    </div>
  );
}

export default function DocsIndex() {
  return (
    <>
    <SEOMetadata title="Setting Up Your Environment" description="Create an API key in the dashboard here, which you'll use to authenticate all requests to the API."  canonicalUrl="https://axionquant.com/docs/00-overview/01-getting-started" />
    <div className="api-documentation lg:max-w-[calc(100vw-340px)] max-w-[calc(100vw-80px)] pb-20 text-gray-700">
      <DocHeading className="text-2xl font-bold text-gray-900 mb-10">Setting Up Your Environment</DocHeading>

      <div className="max-w-[760px]">

        {/* Step 1 */}
        <Step number={1} title="Create an API key">
          <p className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard/keys" className="text-gray-900 underline underline-offset-2 hover:text-gray-600">
              Create an API key in the dashboard here
            </Link>
            , which you'll use to authenticate all requests to the API.
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Store the key as a managed secret and pass it via an environment variable or your app's config.
          </p>
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs text-gray-500">.env</span>
            </div>
            <div className="bg-gray-50">
              <Code lang="bash">AXION_API_KEY=&lt;your_api_key_here&gt;</Code>
            </div>
          </div>
        </Step>

        {/* Step 2 */}
        <Step number={2} title="Install the SDK">
          <p className="text-sm text-gray-600 mb-4">
            Install the official SDK for your language. We'll also use it to load your API key from an environment variable.
          </p>
          <TabCode tabs={{
            Python: {
              lang: 'bash',
              code: `pip install axionquant-sdk python-dotenv`
            },
            npm: {
              lang: 'bash',
              code: `npm i @axionquant/sdk`
            },
            yarn: {
              lang: 'bash',
              code: `yarn add @axionquant/sdk`
            }
          }} />
        </Step>

        {/* Step 3 */}
        <Step number={3} title="Authenticate fetch requests">
          <p className="text-sm text-gray-600 mb-4">
            All API requests require your API key. Include it in one of the following ways - we recommend using the <code className="text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded text-xs">x-api-key</code> header.
          </p>
          <InfoBox>
            Never expose your API key in client-side code. Always make requests from a backend server or use environment variables.
          </InfoBox>
          <TabCode tabs={{
            JavaScript: {
              lang: 'javascript',
              code: `// HTTP Header (Recommended)
const response = await fetch('https://api.axionquant.com/stocks/AAPL', {
  headers: {
    'x-api-key': process.env.AXION_API_KEY
  }
});

// Authorization Bearer
const response = await fetch('https://api.axionquant.com/stocks/AAPL', {
  headers: {
    'Authorization': \`Bearer \${process.env.AXION_API_KEY}\`
  }
});`
            },
            Python: {
              lang: 'python',
              code: `import requests
import os
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

# HTTP Header (Recommended)
headers = {"x-api-key": os.getenv("AXION_API_KEY")}
url = "https://api.axionquant.com/stocks/AAPL"
response = requests.get(url, headers=headers).json()

# Authorization Bearer
headers = {"Authorization": f"Bearer {os.getenv('AXION_API_KEY')}"}`
            },
            Bash: {
              lang: 'bash',
              code: `# HTTP Header (Recommended)
curl -X GET "https://api.axionquant.com/stocks/AAPL" \\
  -H "x-api-key: $AXION_API_KEY"

# Authorization Bearer
curl -X GET "https://api.axionquant.com/stocks/AAPL" \\
  -H "Authorization: Bearer $AXION_API_KEY"`
            }
          }} />
        </Step>

        {/* Step 5 */}
        <Step number={4} title="Make your first SDK request">
          <p className="text-sm text-gray-600 mb-4">Retrieve real-time price data for any stock ticker. This endpoint is available on all plans.</p>
          <TabCode tabs={{
            JavaScript: {
              lang: 'javascript',
              code: `const { Axion } = require('@axionquant/sdk');

const client = new Axion(process.env.AXION_API_KEY);
const ticker = await client.stocks.ticker('AAPL');
console.log(ticker);`
            },
            Python: {
              lang: 'python',
              code: `from axion import Axion

client = Axion(api_key='axn_123')
ticker = client.stocks.ticker('AAPL')
print(ticker)`
            },
            HTTP: {
              lang: 'bash',
              code: `GET https://api.axionquant.com/stocks/AAPL`
            }
          }} />
        </Step>

        {/* Step 5 */}
        <Step number={5} title="Run the code">
          <TabCode tabs={{
            JavaScript: { lang: 'bash', code: `node example.js` },
            Python: { lang: 'bash', code: `python example.py` },
          }} />
          <p className="text-sm text-gray-600 mt-4 mb-4">You should see a response like this:</p>
          <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
            <Code lang="json">{`{
  "symbol": "AAPL",
  "price": 175.34,
  "change": 1.25,
  "changePercent": 0.72,
  "volume": 45678900,
  "marketCap": 2750000000000,
  "timestamp": "2024-01-15T14:30:00Z"
}`}</Code>
          </div>
        </Step>


        {/* Next Steps - no connector line on last item */}
        <div className="flex gap-6">
          <div className="w-8 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Next steps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/docs/01-market-data"
                className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 p-5 transition-colors"
              >
                <Waypoints
                  size={22}
                  className="text-gray-400 group-hover:text-gray-700 transition-colors"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    Market Data
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Real-time and historical prices, quotes, equities, ETFs, indices, and market metrics.
                  </p>
                </div>
              </Link>

              <Link
                to="/docs/02-alternative-data"
                className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 p-5 transition-colors"
              >
                <Database
                  size={22}
                  className="text-gray-400 group-hover:text-gray-700 transition-colors"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    Alternative Data
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    ESG metrics, sentiment signals, supply chain intelligence, and other non-traditional datasets.
                  </p>
                </div>
              </Link>

              <Link
                to="/docs/03-fundamentals"
                className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 p-5 transition-colors"
              >
                <Building2
                  size={22}
                  className="text-gray-400 group-hover:text-gray-700 transition-colors"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    Fundamentals
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Financial statements, earnings, revenue, balance sheets, cash flow, and company metrics.
                  </p>
                </div>
              </Link>

              <Link
                to="/docs/04-macros-and-signals"
                className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 p-5 transition-colors"
              >
                <Activity
                  size={22}
                  className="text-gray-400 group-hover:text-gray-700 transition-colors"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    Macros & Signals
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Economic indicators, macroeconomic data, quantitative signals, and market regime analytics.
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>




      </div>
    </div>
    </>
  );
}
